import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FileText, Link2, Unlink2, Loader2 } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../../App";
import { toast } from "sonner";
import { TypeBadge } from "./TypeBadge";

export function MachineBlueprintLinker({ open, onClose, machine }) {
  const [data, setData] = useState({ linked: [], auto_matched: [] });
  const [allBlueprints, setAllBlueprints] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!machine) return;
    setLoading(true);
    try {
      const [linkResp, bpResp] = await Promise.all([
        axios.get(`${API}/machines/${machine.id}/blueprints`, { headers: getAuthHeaders() }),
        axios.get(`${API}/blueprints`, { headers: getAuthHeaders() }),
      ]);
      setData(linkResp.data);
      const linkedIds = new Set((linkResp.data.linked || []).map(b => b.id));
      const autoIds = new Set((linkResp.data.auto_matched || []).map(b => b.id));
      setAllBlueprints(bpResp.data.filter(b => !linkedIds.has(b.id) && !autoIds.has(b.id)));
    } catch { toast.error("Failed to load blueprint links"); }
    finally { setLoading(false); }
  }, [machine]);

  useEffect(() => {
    if (open) fetchData();
  }, [open, fetchData]);

  const handleLink = async (bpId) => {
    try {
      await axios.post(`${API}/machines/${machine.id}/blueprints/link?blueprint_id=${bpId}`, {}, { headers: getAuthHeaders() });
      toast.success("Blueprint linked");
      fetchData();
    } catch { toast.error("Link failed"); }
  };

  const handleUnlink = async (bpId) => {
    try {
      await axios.delete(`${API}/machines/${machine.id}/blueprints/link?blueprint_id=${bpId}`, { headers: getAuthHeaders() });
      toast.success("Blueprint unlinked");
      fetchData();
    } catch { toast.error("Unlink failed"); }
  };

  if (!machine) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white rounded-none border border-[#E2E8F0] max-w-2xl max-h-[80vh] flex flex-col" aria-describedby="linker-desc">
        <DialogHeader>
          <DialogTitle className="font-heading font-bold text-[#0F172A] flex items-center gap-2">
            <Link2 size={18} /> Blueprints for {machine.machine_number}
          </DialogTitle>
          <p id="linker-desc" className="text-xs text-[#64748B] font-mono">{machine.name}</p>
        </DialogHeader>

        {loading ? (
          <div className="py-8 flex items-center justify-center"><Loader2 size={20} className="animate-spin text-[#2563EB]" /></div>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-4">
            {/* Manually Linked */}
            <div>
              <h3 className="text-xs font-bold text-[#2563EB] uppercase tracking-wide mb-2">Linked ({data.linked.length})</h3>
              {data.linked.length === 0 ? (
                <p className="text-xs text-[#94A3B8]">No blueprints manually linked yet.</p>
              ) : (
                <div className="space-y-2">
                  {data.linked.map(bp => (
                    <div key={bp.id} className="flex items-center justify-between bg-[#F0F9FF] border border-[#BAE6FD] p-3" data-testid={`linked-bp-${bp.id}`}>
                      <div className="flex items-center gap-3">
                        <FileText size={14} className="text-[#2563EB]" />
                        <span className="text-sm text-[#0F172A]">{bp.original_filename}</span>
                        <TypeBadge type={bp.blueprint_type} />
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => handleUnlink(bp.id)} className="text-[#DC2626] hover:bg-[#FEF2F2]" data-testid={`unlink-bp-${bp.id}`}>
                        <Unlink2 size={14} />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Auto-matched */}
            {data.auto_matched.length > 0 && (
              <div>
                <h3 className="text-xs font-bold text-[#10B981] uppercase tracking-wide mb-2">Auto-matched by Machine # ({data.auto_matched.length})</h3>
                <div className="space-y-2">
                  {data.auto_matched.map(bp => (
                    <div key={bp.id} className="flex items-center justify-between bg-[#F0FDF4] border border-[#BBF7D0] p-3" data-testid={`auto-bp-${bp.id}`}>
                      <div className="flex items-center gap-3">
                        <FileText size={14} className="text-[#10B981]" />
                        <span className="text-sm text-[#0F172A]">{bp.original_filename}</span>
                        <TypeBadge type={bp.blueprint_type} />
                      </div>
                      <Button size="sm" variant="outline" onClick={() => handleLink(bp.id)} className="rounded-none border-[#2563EB] text-[#2563EB] text-xs" data-testid={`link-auto-bp-${bp.id}`}>
                        <Link2 size={12} className="mr-1" /> Link
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Available to link */}
            <div>
              <h3 className="text-xs font-bold text-[#64748B] uppercase tracking-wide mb-2">Available Blueprints ({allBlueprints.length})</h3>
              {allBlueprints.length === 0 ? (
                <p className="text-xs text-[#94A3B8]">All blueprints are linked or auto-matched.</p>
              ) : (
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {allBlueprints.map(bp => (
                    <div key={bp.id} className="flex items-center justify-between bg-[#FAFAFA] border border-[#E2E8F0] p-3" data-testid={`available-bp-${bp.id}`}>
                      <div className="flex items-center gap-3">
                        <FileText size={14} className="text-[#64748B]" />
                        <span className="text-sm text-[#0F172A]">{bp.original_filename}</span>
                        <TypeBadge type={bp.blueprint_type} />
                      </div>
                      <Button size="sm" variant="outline" onClick={() => handleLink(bp.id)} className="rounded-none border-[#2563EB] text-[#2563EB] text-xs" data-testid={`link-bp-${bp.id}`}>
                        <Link2 size={12} className="mr-1" /> Link
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
